import React, { useState, useEffect } from 'react';
import { backendUrl, API_KEYS } from '../config';
import { Box, Typography, List, ListItem, ListItemIcon, ListItemText, IconButton, Breadcrumbs, Paper, TextField, Button, Menu, MenuItem, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Divider } from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import StorageIcon from '@mui/icons-material/Storage';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import api from '../services/api';
import { useTranslation } from 'react-i18next';

const FileExplorer = ({ onOpenApp, associations: propAssociations, currentUser }) => {
  const { t } = useTranslation();

  const [currentPath, setCurrentPath] = useState('/');
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [contextFile, setContextFile] = useState(null);
  const [newDialog, setNewDialog] = useState({ open: false, value: '' });
  const [selected, setSelected] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [renameDialog, setRenameDialog] = useState({ open: false, value: '', file: null });
  const [associations, setAssociations] = useState(propAssociations || []);
  const [associationsLoading, setAssociationsLoading] = useState(false);
  const [openFileError, setOpenFileError] = useState('');
  const [copyDialog, setCopyDialog] = useState({ open: false, value: '', src: '', type: '' });
  const [networkDrives, setNetworkDrives] = useState([]);

  let uuid7 = null;
  try {
    const userStr = window.localStorage.getItem('user');
    if (userStr) {
      const user = JSON.parse(userStr);
      uuid7 = user.uuid7 || user.uuid || user.id || null;
    }
  } catch (e) {}

  // Fonction utilitaire pour échapper les caractères spéciaux dans les expressions régulières
  const escapeRegExp = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  // Vérifie si le chemin actuel est un chemin réseau
  const isNetworkPath = (path) => {
    return path.startsWith('/network/');
  };

  // Obtient les informations du lecteur réseau à partir du chemin
  const getNetworkDriveFromPath = (path) => {
    console.log('getNetworkDriveFromPath - path:', path);
    if (!isNetworkPath(path)) {
      console.log('Le chemin n\'est pas un chemin réseau');
      return null;
    }
    
    const parts = path.split('/').filter(Boolean);
    console.log('Parts du chemin:', parts);
    
    if (parts.length < 2) {
      console.log('Chemin réseau invalide: pas assez de parties');
      return null;
    }
    
    const driveLetter = parts[1];
    console.log('Lettre du lecteur:', driveLetter);
    
    const drive = networkDrives.find(d => d.letter === driveLetter);
    if (!drive) {
      console.log('Aucun lecteur trouvé avec la lettre:', driveLetter);
      return null;
    }
    
    console.log('Lecteur trouvé:', drive);
    
    // Nettoyer le chemin du lecteur (supprimer les backslashes et les doublons)
    let cleanDrivePath = (drive.path || '')
      .replace(/^[\\/]+|[\\/]+$/g, '') // Supprimer les slashes de début et fin
      .replace(/\\\\/g, '//') // Remplacer les doubles backslashes par des doubles slashes
      .replace(/\\/g, '/'); // Remplacer les backslashes restants par des slashes
    
    // S'assurer qu'on a exactement deux slashes au début
    if (!cleanDrivePath.startsWith('//')) {
      cleanDrivePath = '//' + cleanDrivePath.replace(/^\/+/, '');
    }
    
    // Extraire le serveur et le partage
    const pathParts = cleanDrivePath.split('/').filter(Boolean);
    if (pathParts.length < 2) {
      console.error('Format de chemin de lecteur réseau invalide:', drive.path);
      return null;
    }
    
    const server = pathParts[0];
    const share = pathParts[1];
    
    // Pour le chemin complet, on utilise directement le chemin du lecteur
    // sans ajouter de chemin relatif supplémentaire
    const fullPath = cleanDrivePath;
    
    console.log('Chemin nettoyé:', {
      originalPath: drive.path,
      cleanDrivePath,
      server,
      share,
      fullPath
    });
    
    return {
      ...drive,
      server: server,
      share: share,
      relPath: '', // Pas de chemin relatif pour la racine
      fullPath: fullPath
    };
  };

  useEffect(() => {
    if (!uuid7) {
      setError(t('fileexplorer.errors.userid'));
      setFiles([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');
    
    const loadFiles = async () => {
      console.log('Chargement des fichiers pour le chemin:', currentPath);
      
      try {
        if (isNetworkPath(currentPath)) {
          console.log('Chemin réseau détecté');
          // Chargement des fichiers réseau
          const drive = getNetworkDriveFromPath(currentPath);
          console.log('Lecteur réseau après getNetworkDriveFromPath:', drive);
          
          if (!drive) {
            const errorMsg = t('fileexplorer.errors.network_drive_not_found');
            console.error(errorMsg);
            setError(errorMsg);
            return;
          }
          
          try {
            console.log('Tentative de connexion au lecteur réseau:', drive);
            console.log('Chemin complet du lecteur (fullPath):', drive.fullPath);
            console.log('Structure complète du lecteur:', JSON.stringify(drive, null, 2));
            
            // Préparer les paramètres de la requête
            const params = {
              path: drive.fullPath
            };
            
            // Ajouter les informations d'authentification directement dans les paramètres
            // car le backend les attend comme paramètres de requête
            if (drive.username && drive.password) {
              params.username = drive.username;
              params.password = drive.password;
              // Ajouter également le domaine si disponible
              if (drive.domain) {
                params.domain = drive.domain;
              }
              // Spécifier le port SMB (par défaut 445)
              params.port = 445;
            }
            
            console.log('Configuration de la requête:', {
              ...params,
              password: params.password ? '[REDACTED]' : undefined
            });
            
            // Faire la requête avec les paramètres d'authentification
            const response = await api.get('/api/network-files', { params });
            
            // Formater les fichiers pour correspondre au format attendu
            const formattedFiles = (response.data || []).map(item => ({
              name: item.name,
              type: item.type === 'directory' ? 'folder' : 'file',
              size: item.size,
              modified: item.modified,
              path: `${currentPath}${currentPath.endsWith('/') ? '' : '/'}${item.name}`
            }));
            
            console.log('Fichiers chargés avec succès:', formattedFiles);
            setFiles(formattedFiles);
          } catch (err) {
            console.error('Erreur chargement fichiers réseau:', err);
            const errorMessage = err.response?.data?.detail || err.message || 'Erreur inconnue';
            setError(`Erreur lors du chargement des fichiers réseau: ${errorMessage}`);
          }
        } else {
          // Chargement des fichiers locaux
          let relPath = currentPath === '/' ? '' : currentPath.replace(/^\//, '');
          const response = await api.get(`/files?uuid7=${encodeURIComponent(uuid7)}&path=${encodeURIComponent(relPath)}`);
          setFiles(response.data);
        }
      } catch (error) {
        console.error('Error loading files:', error);
        setError(t(isNetworkPath(currentPath) ? 'fileexplorer.errors.load_network' : 'fileexplorer.errors.load'));
      } finally {
        setLoading(false);
      }
    };

    loadFiles();
  }, [currentPath, uuid7]);

  useEffect(() => {
    // Charger les associations
    const loadAssociations = async () => {
      if (propAssociations) return;
      setAssociationsLoading(true);
      try {
        const response = await fetch(`${backendUrl}/api/config/associations`, {
          headers: {
            'x-api-key': API_KEYS.key,
            'x-api-secret': API_KEYS.secret,
            'accept': 'application/json'
          }
        });
        const data = await response.json();
        if (data && Array.isArray(data.associations)) {
          setAssociations(data.associations);
        }
      } catch (error) {
        setOpenFileError(t('fileexplorer.errors.associations'));
      } finally {
        setAssociationsLoading(false);
      }
    };

    // Charger les lecteurs réseau au montage du composant
    console.log('Chargement des lecteurs réseau...');
    const loadNetworkDrives = async () => {
      try {
        const response = await api.get('/app-groups/1/network-drives');
        console.log('Lecteurs réseau chargés:', JSON.stringify(response.data, null, 2));
        setNetworkDrives(response.data || []);
        
        // Vérifier la structure des lecteurs
        if (response.data && response.data.length > 0) {
          console.log('Structure du premier lecteur:', JSON.stringify(response.data[0], null, 2));
        }
      } catch (error) {
        console.error('Erreur lors du chargement des disques réseau:', error);
      }
    };

    loadAssociations();
    loadNetworkDrives();
  }, [propAssociations, currentUser]);

  const goUp = () => {
    if (currentPath === '/') return;
    const parts = currentPath.split('/').filter(Boolean);
    parts.pop();
    setCurrentPath(parts.length === 0 ? '/' : '/' + parts.join('/'));
  };

  const openFolder = (name) => {
  //console.log('[FileExplorer] openFolder', name);
    setCurrentPath(currentPath === '/' ? `/${name}` : `${currentPath}/${name}`);
  };

  const handleMenu = (event, file) => {
  //console.log('[FileExplorer] handleMenu', file, event);
  if (event && event.stopPropagation) event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setContextFile(file);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setContextFile(null);
  };

  const handleNewFolder = () => {
    setNewDialog({ open: true, value: '' });
  };

  const confirmNewFolder = () => {
    if (!newDialog.value.trim()) return;
    
    if (isNetworkPath(currentPath)) {
      // Création de dossier sur un lecteur réseau
      const drive = getNetworkDriveFromPath(currentPath);
      if (!drive) {
        setError('Lecteur réseau non trouvé');
        return;
      }
      
      // Construire le chemin complet
      const newFolderPath = `${currentPath}/${newDialog.value.trim()}`.replace(/\/+/g, '/');
      const fullPath = `//${drive.path}${newFolderPath.replace(/^\/network\/[^/]+\//, '')}`;
      
      setLoading(true);
      api.post('/network-files/create-folder', { path: fullPath })
        .then(() => {
          // Rafraîchir la liste
          const updatedFiles = [...files, { 
            name: newDialog.value.trim(), 
            type: 'folder',
            path: newFolderPath
          }];
          setFiles(updatedFiles);
          setNewDialog({ open: false, value: '' });
        })
        .catch(err => {
          console.error('Erreur création dossier réseau:', err);
          setError('Erreur lors de la création du dossier réseau');
        })
        .finally(() => setLoading(false));
    } else {
      // Code existant pour les dossiers locaux
      let relPath = currentPath === '/' ? '' : currentPath.replace(/^\//, '');
      let fullPath = relPath ? relPath + '/' + newDialog.value.trim() : newDialog.value.trim();
      setLoading(true);
      api.post('/create-folder', { uuid7, path: fullPath })
        .then(() => {
          setFiles(f => [...f, { name: newDialog.value.trim(), type: 'folder' }]);
          setNewDialog({ open: false, value: '' });
        })
        .catch(() => setError(t('fileexplorer.errors.create')))
        .finally(() => setLoading(false));
    }
  };

  const handleDelete = () => {
    setConfirmDelete(true);
    setMenuAnchor(null);
  };

  const doDelete = () => {
    if (!contextFile) return setConfirmDelete(false);
    
    if (isNetworkPath(currentPath)) {
      // Suppression sur un lecteur réseau
      const drive = getNetworkDriveFromPath(currentPath);
      if (!drive) {
        setError('Lecteur réseau non trouvé');
        return;
      }
      
      const filePath = `${currentPath}/${contextFile.name}`.replace(/\/+/g, '/');
      const fullPath = `//${drive.path}${filePath.replace(/^\/network\/[^/]+\//, '')}`;
      
      setLoading(true);
      const isFolder = contextFile.type === 'folder';
      const endpoint = isFolder ? '/network-files/delete-folder' : '/network-files/delete-file';
      
      api.delete(endpoint, { data: { path: fullPath } })
        .then(() => {
          setFiles(f => f.filter(file => file.name !== contextFile.name));
          setContextFile(null);
        })
        .catch(err => {
          console.error('Erreur suppression réseau:', err);
          setError(`Erreur lors de la suppression ${isFolder ? 'du dossier' : 'du fichier'} réseau`);
        })
        .finally(() => {
          setLoading(false);
          setConfirmDelete(false);
        });
    } else {
      // Code existant pour les fichiers locaux
      let relPath = currentPath === '/' ? '' : currentPath.replace(/^\//, '');
      let fullPath = relPath ? relPath + '/' + contextFile.name : contextFile.name;
      setLoading(true);
      const isFolder = contextFile.type === 'folder';
      const url = isFolder ? '/file/delete-folder' : '/file/delete-file';
      
      api({
        url,
        method: 'DELETE',
        data: { uuid7, path: fullPath }
      })
        .then(() => {
          setFiles(f => f.filter(file => file.name !== contextFile.name));
          setContextFile(null);
        })
        .catch(() => setError(`Erreur lors de la suppression du ${isFolder ? 'dossier' : 'fichier'}.`))
        .finally(() => {
          setLoading(false);
          setConfirmDelete(false);
        });
    }
  };

  const handleRename = () => {
    setRenameDialog({ open: true, value: contextFile?.name || '', file: contextFile });
    setMenuAnchor(null);
  };

  const doRename = () => {
    if (!renameDialog.file || !renameDialog.value.trim() || renameDialog.value === renameDialog.file.name) {
      setRenameDialog({ open: false, value: '', file: null });
      return;
    }

    if (isNetworkPath(currentPath)) {
      // Renommage sur un lecteur réseau
      const drive = getNetworkDriveFromPath(currentPath);
      if (!drive) {
        setError('Lecteur réseau non trouvé');
        return;
      }

      const oldPath = `${currentPath}/${renameDialog.file.name}`.replace(/\/+/g, '/');
      const newPath = `${currentPath}/${renameDialog.value.trim()}`.replace(/\/+/g, '/');
      
      const fullOldPath = `//${drive.path}${oldPath.replace(/^\/network\/[^/]+\//, '')}`;
      const fullNewPath = `//${drive.path}${newPath.replace(/^\/network\/[^/]+\//, '')}`;

      setLoading(true);
      api.put('/api/network-files/rename', { 
        old_path: fullOldPath,
        new_path: fullNewPath
      })
        .then(() => {
          // Mettre à jour la liste des fichiers
          setFiles(files.map(f => 
            f.name === renameDialog.file.name ? 
            { ...f, name: renameDialog.value.trim() } : f
          ));
          setRenameDialog({ open: false, value: '', file: null });
        })
        .catch(err => {
          console.error('Erreur renommage réseau:', err);
          setError('Erreur lors du renommage sur le réseau');
        })
        .finally(() => setLoading(false));
    } else {
      // Code existant pour les fichiers locaux
      let relPath = currentPath === '/' ? '' : currentPath.replace(/^\//, '');
      let old_path = relPath ? relPath + '/' + renameDialog.file.name : renameDialog.file.name;
      let new_path = relPath ? relPath + '/' + renameDialog.value.trim() : renameDialog.value.trim();
      setLoading(true);
      api.put('/file/modify-file', { uuid7, old_path, new_path })
        .then(() => {
          setRenameDialog({ open: false, value: '', file: null });
          // Rafraîchir la liste après renommage
          let relPathRefresh = currentPath === '/' ? '' : currentPath.replace(/^\//, '');
          api.get(`/files?uuid7=${encodeURIComponent(uuid7)}&path=${encodeURIComponent(relPathRefresh)}`)
            .then(res => setFiles(res.data))
            .catch(() => setError(t('fileexplorer.errors.refresh')));
        })
        .catch(() => setError('Erreur lors du renommage.'))
        .finally(() => setLoading(false));
    }
  };

  const handleFileOpen = async (file) => {
    setOpenFileError('');
    if (!file || file.type !== 'file') return;
    
    // Extraire l'extension
    const extMatch = file.name.match(/\.([a-zA-Z0-9]+)$/);
    const ext = extMatch ? extMatch[1].toLowerCase() : '';
    if (!ext) {
      setOpenFileError(t('fileexplorer.errors.extension'));
      return;
    }

    // Chercher l'association
    const assoc = associations.find(a => {
      if (!a.ext) return false;
      return a.ext.toLowerCase() === `.${ext}` || a.ext.toLowerCase() === ext;
    });

    if (!assoc?.app) {
      setOpenFileError(t('fileexplorer.errors.noapp', { ext }));
      return;
    }

    try {
      let fileContent = '';
      
      if (isNetworkPath(currentPath)) {
        // Télécharger le fichier réseau
        const drive = getNetworkDriveFromPath(currentPath);
        if (!drive) {
          setError('Lecteur réseau non trouvé');
          return;
        }
        
        // Construire le chemin complet en utilisant le chemin du lecteur et le nom du fichier
        const fileName = file.name.replace(/^\/network\/[^/]+\//, '');
        const fullPath = `${drive.fullPath}/${fileName}`.replace(/([^:])\/+/g, '$1/');
        
        const response = await api.get(`/api/network-files/download?path=${encodeURIComponent(fullPath)}`, {
          responseType: 'blob'
        });
        fileContent = await response.data.text();
      } else {
        // Télécharger le fichier local
        let relPath = currentPath === '/' ? '' : currentPath.replace(/^\//, '');
        let filePath = relPath ? `${relPath}/${file.name}` : file.name;
        const response = await api.get(`/download-file?uuid7=${encodeURIComponent(uuid7)}&path=${encodeURIComponent(filePath)}`);
        fileContent = response.data?.content || '';
      }
      
      // Stocker le chemin et les données pour l'application
      window.localStorage.setItem('notepadApp_filePath', file.name);
      window.localStorage.setItem('notepadApp_fileContent', fileContent);
      
      // Ouvrir l'application associée
      if (typeof onOpenApp === 'function') {
        onOpenApp(assoc.app);
      }
    } catch (error) {
      console.error('Erreur ouverture fichier:', error);
      setOpenFileError(t('fileexplorer.errors.openfile') + ': ' + (error.message || 'Erreur inconnue'));
    }
  };

  const handleCopy = (file) => {
    if (!file) return;
    
    if (isNetworkPath(currentPath)) {
      const drive = getNetworkDriveFromPath(currentPath);
      if (!drive) {
        setError('Lecteur réseau non trouvé');
        return;
      }
      
      const filePath = `${currentPath}/${file.name}`.replace(/\/+/g, '/');
      const fullPath = `//${drive.path}${filePath.replace(/^\/network\/[^/]+\//, '')}`;
      
      window.localStorage.setItem('fileExplorer_copy_src', fullPath);
      window.localStorage.setItem('fileExplorer_copy_type', file.type);
      window.localStorage.setItem('fileExplorer_copy_source', 'network'); // Marquer comme copie réseau
      setOpenFileError(t('fileexplorer.copy') + ' : ' + file.name);
    } else {
      let relPath = currentPath === '/' ? '' : currentPath.replace(/^\//, '');
      let srcPath = relPath ? relPath + '/' + file.name : file.name;
      window.localStorage.setItem('fileExplorer_copy_src', srcPath);
      window.localStorage.setItem('fileExplorer_copy_type', file.type);
      window.localStorage.setItem('fileExplorer_copy_source', 'local'); // Marquer comme copie locale
      setOpenFileError(t('fileexplorer.copy') + ' : ' + file.name);
    }
  };

  const handlePaste = async () => {
    const srcPath = window.localStorage.getItem('fileExplorer_copy_src');
    const srcType = window.localStorage.getItem('fileExplorer_copy_type');
    const copySource = window.localStorage.getItem('fileExplorer_copy_source');
    
    if (!srcPath) {
      setOpenFileError(t('fileexplorer.errors.paste'));
      return;
    }

    if (isNetworkPath(currentPath) && copySource === 'network') {
      // Coller sur le réseau (même lecteur réseau)
      const drive = getNetworkDriveFromPath(currentPath);
      if (!drive) {
        setError('Lecteur réseau non trouvé');
        return;
      }

      const fileName = srcPath.split('/').pop();
      setCopyDialog({ 
        open: true, 
        value: fileName, 
        src: srcPath, 
        type: srcType,
        isNetwork: true,
        driveLetter: drive.letter
      });
    } else if (!isNetworkPath(currentPath) && copySource === 'local') {
      // Coller en local (comportement existant)
      let defaultName = srcPath.split('/').pop();
      setCopyDialog({ 
        open: true, 
        value: defaultName, 
        src: srcPath, 
        type: srcType,
        isNetwork: false
      });
    } else {
      // Opération de copie entre système de fichiers non supportée
      setOpenFileError('Le collage entre systèmes de fichiers différents n\'est pas supporté');
    }
  };

  const handleCopyDialogValidate = async () => {
    const { src, value, type, isNetwork, driveLetter } = copyDialog;
    if (!src || !value.trim()) {
      setCopyDialog({ open: false, value: '', src: '', type: '' });
      return;
    }
    
    try {
      setLoading(true);
      
      if (isNetwork) {
        // Copier sur un lecteur réseau
        const drive = networkDrives.find(d => d.letter === driveLetter);
        if (!drive) {
          setError('Lecteur réseau non trouvé');
          return;
        }

        // Construire le chemin de destination
        let destPath = currentPath === '/' ? '' : currentPath.replace(/^\//, '');
        destPath = destPath ? `${destPath}/${value.trim()}` : value.trim();
        const fullDestPath = `//${drive.path}${destPath ? '/' + destPath : ''}`;

        // Vérifier si la destination existe déjà
        const exists = files.some(f => f.name === value.trim());
        if (exists) {
          setOpenFileError(t('fileexplorer.errors.exists'));
          return;
        }

        // Copier le fichier sur le réseau
        await api.post('/api/network-files/copy', {
          source_path: src,
          destination_path: fullDestPath
        });

        // Rafraîchir la liste
        const response = await api.get(`/api/network-files?path=//${drive.path}${currentPath.replace(/^\/network\/[^/]+\//, '')}`);
        setFiles(response.data || []);
        
        setOpenFileError(t('fileexplorer.copy_success') + ': ' + value.trim());
      } else {
        // Code existant pour les fichiers locaux
        let relPath = currentPath === '/' ? '' : currentPath.replace(/^\//, '');
        let destPath = relPath ? `${relPath}/${value.trim()}` : value.trim();
        
        // Vérifier si la destination existe déjà
        const exists = files.some(f => f.name === value.trim());
        if (exists) {
          setOpenFileError(t('fileexplorer.errors.exists'));
          return;
        }
        
        // Télécharger le fichier source
        const srcResponse = await api.get(`/download-file?uuid7=${encodeURIComponent(uuid7)}&path=${encodeURIComponent(src)}`, 
          { responseType: 'blob' });
        
        // Créer un nouveau fichier FormData pour l'upload
        const formData = new FormData();
        formData.append('uuid7', uuid7);
        formData.append('path', destPath);
        formData.append('file', new Blob([srcResponse.data]), value.trim());
        
        // Uploader la copie
        await api.post('/upload-file', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        // Rafraîchir la liste
        const refreshed = await api.get(`/files?uuid7=${encodeURIComponent(uuid7)}&path=${encodeURIComponent(relPath)}`);
        setFiles(refreshed.data);
        setOpenFileError(t('fileexplorer.copy_success') + ': ' + value.trim());
      }
    } catch (error) {
      console.error('Erreur lors de la copie:', error);
      setOpenFileError(t('fileexplorer.errors.copy') + ': ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
      setCopyDialog({ open: false, value: '', src: '', type: '' });
      window.localStorage.removeItem('fileExplorer_copy_src');
      window.localStorage.removeItem('fileExplorer_copy_type');
      window.localStorage.removeItem('fileExplorer_copy_source');
    }
  };

  const handleCopyDialogCancel = () => setCopyDialog({ open: false, value: '', src: '', type: '' });

  // Breadcrumbs logic
  const pathParts = currentPath.split('/').filter(Boolean);
  const breadcrumbPaths = pathParts.reduce((acc, part) => {
    const prev = acc.length > 0 ? acc[acc.length - 1] : '';
    acc.push(prev + '/' + part);
    return acc;
  }, []);

  // Filter files by search
  const filteredFiles = files.filter(f => !search || f.name.toLowerCase().includes(search.toLowerCase()));

  // Fonction pour ouvrir un disque réseau
  const openNetworkDrive = (drive) => {
    // Utiliser une URL spéciale pour identifier un disque réseau
    // Le format est /network/<lettre_lecteur>/
    // Le chemin du lecteur sera extrait de drive.path dans getNetworkDriveFromPath
    console.log('Ouverture du lecteur réseau:', drive.letter);
    setCurrentPath(`/network/${drive.letter}/`);
  };

  // Fonction pour naviguer dans un dossier réseau
  const openNetworkFolder = (folderName) => {
    if (!isNetworkPath(currentPath)) return;
    
    const drive = getNetworkDriveFromPath(currentPath);
    if (!drive) return;
    
    // Construire le nouveau chemin en ajoutant le dossier au chemin actuel
    const currentParts = currentPath.split('/').filter(Boolean);
    const newPath = [...currentParts, folderName].join('/');
    setCurrentPath('/' + newPath);
  };
  
  // Fonction pour ouvrir un fichier ou un dossier
  const openItem = (item) => {
    if (item.type === 'folder') {
      if (isNetworkPath(currentPath)) {
        openNetworkFolder(item.name);
      } else {
        openFolder(item.name);
      }
    } else {
      handleFileOpen(item);
    }
  };

  return (
    <Paper sx={{ p: 2, minWidth: 350 }}>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <Button size="small" variant="outlined" onClick={goUp} onTouchStart={e => { e.stopPropagation(); goUp(); }} disabled={currentPath === '/'} startIcon={<ArrowUpwardIcon />}>{t('fileexplorer.up')}</Button>
        <TextField
          value={currentPath}
          size="small"
          label={t('fileexplorer.path')}
          sx={{ flex: 1 }}
          InputProps={{ readOnly: true }}
        />
        <TextField
          value={search}
          onChange={e => setSearch(e.target.value)}
          size="small"
          label={t('fileexplorer.search')}
        />
        <IconButton onClick={handleNewFolder} title={t('fileexplorer.newfolder')}><AddIcon /></IconButton>
      </Box>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <span style={{ cursor: 'pointer', color: '#1976d2' }} onClick={() => setCurrentPath('/')}>{t('fileexplorer.root')}</span>
        {pathParts.map((part, idx) => (
          <span
            key={idx}
            style={{ cursor: 'pointer', color: '#1976d2' }}
            onClick={() => setCurrentPath(breadcrumbPaths[idx])}
          >{part}</span>
        ))}
      </Breadcrumbs>
      
      {/* Affichage des disques réseau */}
      {currentPath === '/' && networkDrives.length > 0 && (
        <Paper elevation={3} sx={{ mb: 3, p: 2, bgcolor: 'background.paper' }}>
          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
            {t('fileexplorer.network_drives')}
          </Typography>
          <List>
            {networkDrives.map((drive) => (
              <ListItem 
                key={drive.id}
                button 
                onClick={() => openNetworkDrive(drive)}
                sx={{ 
                  '&:hover': { 
                    bgcolor: 'action.hover',
                    borderRadius: 1
                  },
                  mb: 1,
                  borderRadius: 1,
                  transition: 'background-color 0.2s',
                  border: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <StorageIcon color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary={`${drive.letter}:`} 
                  secondary={`${drive.path} (${drive.type})`}
                  primaryTypographyProps={{
                    fontWeight: 'medium',
                    color: 'text.primary'
                  }}
                  secondaryTypographyProps={{
                    color: 'text.secondary',
                    variant: 'body2'
                  }}
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}

      {/* Séparateur */}
      {currentPath === '/' && networkDrives.length > 0 && (
        <Divider sx={{ my: 2, '&::before, &::after': { borderColor: 'divider' } }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', px: 2, bgcolor: 'background.paper' }}>
            {t('fileexplorer.local_files')}
          </Typography>
        </Divider>
      )}

      <Box minHeight={0} overflow="auto">
        {loading ? (
          <Typography>Chargement...</Typography>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : (
          <List>
            {filteredFiles.map((file, idx) => (
              <ListItem
                key={idx}
                button
                onDoubleClick={() => openItem(file)}
                onTouchStart={e => {
                  // Démarre le timer d'appui long
                  if (window.longTouchTimer) clearTimeout(window.longTouchTimer);
                  window.touchStartTime = Date.now();
                  window.longTouchFired = false;
                  window.longTouchMoved = false;
                  window.longTouchTimer = setTimeout(() => {
                    window.longTouchFired = true;
                    handleMenu(e, file);
                  }, 500); // 500ms = appui long
                }}
                onTouchMove={e => {
                  window.longTouchMoved = true;
                  if (window.longTouchTimer) {
                    clearTimeout(window.longTouchTimer);
                    window.longTouchTimer = null;
                  }
                }}
                onTouchEnd={e => {
                  if (window.longTouchTimer) {
                    clearTimeout(window.longTouchTimer);
                    window.longTouchTimer = null;
                  }
                  // Si ce n'est pas un appui long ET pas un mouvement, ouvrir/ouvrir dossier
                  if (!window.longTouchFired && !window.longTouchMoved) {
                    if (file.type === 'folder') openFolder(file.name);
                    else handleFileOpen(file);
                  }
                  window.longTouchFired = false;
                  window.longTouchMoved = false;
                }}
                onTouchCancel={e => {
                  if (window.longTouchTimer) {
                    clearTimeout(window.longTouchTimer);
                    window.longTouchTimer = null;
                  }
                  window.longTouchFired = false;
                  window.longTouchMoved = false;
                }}
                secondaryAction={
                  <IconButton onClick={e => handleMenu(e, file)}>
                    <MoreVertIcon />
                  </IconButton>
                }
              >
                <ListItemIcon>
                  {file.type === 'folder' ? <FolderIcon color="primary" /> : <InsertDriveFileIcon />}
                </ListItemIcon>
                <ListItemText primary={file.name} />
              </ListItem>
            ))}
            {filteredFiles.length === 0 && (<ListItem><ListItemText primary={t('fileexplorer.empty')} /></ListItem>)}
          </List>
        )}
      </Box>
      {openFileError && (
        <Typography color="error" sx={{ mt: 1 }}>{openFileError}</Typography>
      )}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        {/* Action ouvrir si fichier */}
        {contextFile && contextFile.type === 'file' && (
          <MenuItem onClick={() => { handleFileOpen(contextFile); handleMenuClose(); }}>
            <InsertDriveFileIcon fontSize="small" sx={{ mr: 1 }} />{t('fileexplorer.open')}
          </MenuItem>
        )}
        <MenuItem onClick={handleRename}>
          <DriveFileRenameOutlineIcon fontSize="small" sx={{ mr: 1 }} />{t('fileexplorer.rename')}
        </MenuItem>
        <MenuItem onClick={handleDelete}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />{t('fileexplorer.delete')}
        </MenuItem>
        {contextFile && (
          <MenuItem onClick={() => { handleCopy(contextFile); handleMenuClose(); }}>
            <AddIcon fontSize="small" sx={{ mr: 1 }} />{t('fileexplorer.copy')}
          </MenuItem>
        )}
      </Menu>
      <Button variant="outlined" size="small" sx={{ mt: 1, mb: 1, ml: 2 }} onClick={handlePaste}>
        {t('fileexplorer.paste')}
      </Button>
      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle>{t('fileexplorer.confirmdelete')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('fileexplorer.confirmdelete.text')} {contextFile?.type === 'folder' ? t('fileexplorer.folder') : t('fileexplorer.file')} <b> {contextFile?.name}</b> ? {t('fileexplorer.confirmdelete.irreversible')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)} color="primary">
            {t('fileexplorer.cancel')}
          </Button>
          <Button onClick={doDelete} color="error" variant="contained">
            {t('fileexplorer.delete')}
          </Button>
        </DialogActions>
      </Dialog>
      <Menu
        open={newDialog.open}
        anchorEl={null}
        onClose={() => setNewDialog({ open: false, value: '' })}
        anchorReference="none"
        sx={{ '& .MuiMenu-paper': { padding: 2 } }}
      >
        <Box display="flex" alignItems="center" gap={2} p={2}>
          <TextField
            label={t('fileexplorer.foldername')}
            value={newDialog.value}
            onChange={e => setNewDialog(d => ({ ...d, value: e.target.value }))}
            size="small"
          />
          <Button variant="contained" onClick={confirmNewFolder}>{t('fileexplorer.create')}</Button>
        </Box>
      </Menu>
      <Dialog open={renameDialog.open} onClose={() => setRenameDialog({ open: false, value: '', file: null })}>
        <DialogTitle>{t('fileexplorer.rename')} {renameDialog.file?.type === 'folder' ? t('fileexplorer.folder') : t('fileexplorer.file')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('fileexplorer.newname')} <b>{renameDialog.file?.name}</b> :
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label={t('fileexplorer.newname')}
            value={renameDialog.value}
            onChange={e => setRenameDialog(d => ({ ...d, value: e.target.value }))}
            fullWidth
            variant="standard"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameDialog({ open: false, value: '', file: null })} color="primary">
            {t('fileexplorer.cancel')}
          </Button>
          <Button onClick={doRename} color="primary" variant="contained">
            {t('fileexplorer.rename')}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={copyDialog.open} onClose={handleCopyDialogCancel}>
        <DialogTitle>{t('fileexplorer.copy')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('fileexplorer.copyname')} :
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label={t('fileexplorer.copyname')}
            value={copyDialog.value}
            onChange={e => setCopyDialog(d => ({ ...d, value: e.target.value }))}
            fullWidth
            variant="standard"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCopyDialogCancel} color="primary">{t('fileexplorer.cancel')}</Button>
          <Button onClick={handleCopyDialogValidate} color="primary" variant="contained">{t('fileexplorer.validate')}</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default FileExplorer;